import { useState, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import { Label } from '../ui/label'

// The script regexes have ONE definition, in lib/validation.ts — there used to
// be three (here, the onboarding page, and nowhere at all on the surfaces that
// needed them most). This component keeps its own i18n messages; what it no
// longer keeps is its own idea of what Arabic is.
import { ARABIC_RE as AR_RE, LATIN_RE } from '../../lib/validation'

// Pure digits (whole string is numbers only)
const PURE_NUM_RE = /^\d+$/

type Lang = 'en' | 'ar' | 'any'

export interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  /** Field label */
  label: string
  /** Controlled value */
  value: string
  /** Change handler — receives the new string */
  onChange: (value: string) => void
  /** Expected script for the field.
   *  'en'  → rejects Arabic characters
   *  'ar'  → requires Arabic and rejects Latin letters (one alphabet per field)
   *  'any' → no language restriction (default)
   */
  lang?: Lang
  /** Maximum allowed character length */
  maxLength?: number
  /** Marks the field as required (shows error when left blank on blur) */
  required?: boolean
  /** External error message (overrides internal validation) */
  externalError?: string
  /** Extra className on the wrapper div */
  wrapperClassName?: string
}

function validate(
  value: string,
  lang: Lang,
  maxLength: number | undefined,
  required: boolean,
  t: (k: string) => string,
): string | null {
  const trimmed = value.trim()

  if (required && trimmed === '') return t('validation.required')

  if (trimmed === '') return null // empty + not required → no error

  if (maxLength !== undefined && value.length > maxLength)
    return t('validation.maxLength').replace('{{max}}', String(maxLength))

  if (PURE_NUM_RE.test(trimmed))
    return t('validation.noNumbers')

  if (lang === 'en' && AR_RE.test(trimmed))
    return t('validation.englishOnly')

  if (lang === 'ar' && !AR_RE.test(trimmed))
    return t('validation.arabicOnly')

  // One alphabet per field — see lib/validation.ts.
  if (lang === 'ar' && LATIN_RE.test(trimmed))
    return t('validation.noMixedScript')

  return null
}

export function TextInput({
  label,
  value,
  onChange,
  lang = 'any',
  maxLength,
  required = false,
  externalError,
  wrapperClassName,
  className,
  placeholder,
  ...rest
}: TextInputProps) {
  const { t, i18n } = useTranslation()
  const id = useId()
  const [touched, setTouched] = useState(false)

  const isRtl = i18n.dir() === 'rtl'
  // For 'ar' fields always use rtl; for 'en' fields always ltr; 'any' follows UI direction
  const dir = lang === 'ar' ? 'rtl' : lang === 'en' ? 'ltr' : isRtl ? 'rtl' : 'ltr'

  const internalError = touched ? validate(value, lang, maxLength, required, t) : null
  const error = externalError ?? internalError

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const handleBlur = () => setTouched(true)

  return (
    <div className={cn('grid gap-1.5', wrapperClassName)}>
      <Label htmlFor={id} className={error ? 'text-destructive' : ''}>
        {label}
        {required && <span className="text-destructive ms-0.5">*</span>}
      </Label>
      <input
        id={id}
        dir={dir}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        maxLength={maxLength ? maxLength + 20 : undefined} /* let JS validate, not browser hard-cut */
        placeholder={placeholder}
        className={cn(
          'flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm transition-colors',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1',
          error
            ? 'border-destructive focus-visible:ring-destructive'
            : 'border-input focus-visible:ring-ring',
          className,
        )}
        {...rest}
      />
      {error ? (
        <p className="text-[11px] text-destructive leading-tight">{error}</p>
      ) : maxLength ? (
        // Always rendered once a field has a cap, so the counter appearing on
        // the first keystroke does not push everything below it down a line
        // while someone is mid-word — on a phone that moved the field out
        // from under the thumb.
        <p className={cn(
          'min-h-[11px] text-[11px] leading-tight tabular-nums',
          value.length > maxLength * 0.9 ? 'text-amber-500' : 'text-muted-foreground',
        )}>
          {value.length > 0 ? `${value.length}/${maxLength}` : '\u00a0'}
        </p>
      ) : null}
    </div>
  )
}
